import {useState, useMemo} from 'react'

import {ActionList, ActionMenu, Text} from '@primer/react'
import {useSearchParams} from '@github-ui/use-navigate'

export default function Filters() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filterOptions = {
    all: 'All',
    free_trial: 'Free trial',
  }
  const [activeFilter, setActiveFilter] = useState(filterOptions.all)

  const creatorOptions = ['All creators', 'Verified creators']
  const [activeCreators, setActiveCreators] = useState(creatorOptions[0])

  const sortValue = useMemo(() => {
    const queryString = searchParams.get('query')
    if (!queryString) {
      return null
    }

    const match = queryString.match(/sort:([^ ]*)/)
    return match ? match[1] : null
  }, [searchParams])

  const sortOptions = useMemo(() => {
    return {
      'popularity-desc': 'Most installed/starred',
      'created-desc': 'Recently added',
      'match-desc': 'Best match',
    }
  }, [])

  const [activeSort, setActiveSort] = useState('popularity-desc')

  const queryWithoutSort = useMemo(() => {
    const queryString = searchParams.get('query')
    if (!queryString) {
      return ''
    }

    return queryString.replace(/sort:([^ ]*)/, '').trim()
  }, [searchParams])

  useMemo(() => {
    if (searchParams.has('verification')) {
      const verification = searchParams.get('verification')
      if (verification === 'verified') {
        setActiveCreators('Verified creators')
      }
    }

    if (searchParams.has('filter')) {
      const filter = searchParams.get('filter')
      if (filter === 'free_trial') {
        setActiveFilter(filterOptions.free_trial)
      }
    }

    switch (sortValue) {
      case 'popularity-desc':
        setActiveSort('popularity-desc')
        break
      case 'created-desc':
        setActiveSort('created-desc')
        break
      case 'match-desc':
        setActiveSort('match-desc')
        break
      default:
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sortText = useMemo(() => {
    switch (activeSort) {
      case 'popularity-desc':
        return sortOptions['popularity-desc']
      case 'created-desc':
        return sortOptions['created-desc']
      case 'match-desc':
        return sortOptions['match-desc']
      default:
        return ''
    }
  }, [activeSort, sortOptions])

  const onCreatorChange = (creator: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (creator === 'Verified creators') {
      newParams.set('verification', 'verified')
    } else {
      newParams.delete('verification')
    }
    setSearchParams(newParams)
    setActiveCreators(creator)
  }

  const onFilterChange = (filter: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (filter === filterOptions.free_trial) {
      newParams.set('filter', 'free_trial')
    } else if (filter === filterOptions.all) {
      newParams.delete('filter')
    }
    setSearchParams(newParams)
    setActiveFilter(filter)
  }

  const onSortChange = (sort: string) => {
    setActiveSort(sort)
    const newParams = new URLSearchParams(searchParams)
    newParams.set('query', `${queryWithoutSort} sort:${sort}`.trim())
    setSearchParams(newParams)
  }

  return (
    <div className="d-flex gap-2 flex-wrap">
      <ActionMenu>
        <ActionMenu.Button>
          <Text className="fgColor-muted">Filter:</Text> {activeFilter}
        </ActionMenu.Button>
        <ActionMenu.Overlay width="small" align="end">
          <ActionList selectionVariant="single">
            {Object.entries(filterOptions).map(([key, value]) => (
              <ActionList.Item key={key} selected={value === activeFilter} onSelect={() => onFilterChange(value)}>
                {value}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <ActionMenu>
        <ActionMenu.Button>
          <Text className="fgColor-muted">By:</Text> {activeCreators}
        </ActionMenu.Button>
        <ActionMenu.Overlay width="small" align="end">
          <ActionList selectionVariant="single">
            {creatorOptions.map(option => (
              <ActionList.Item
                key={option}
                selected={option === activeCreators}
                onSelect={() => onCreatorChange(option)}
              >
                {option}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <ActionMenu>
        <ActionMenu.Button>
          <Text className="fgColor-muted">Sort:</Text> {sortText}
        </ActionMenu.Button>
        <ActionMenu.Overlay width="medium" align="end">
          <ActionList selectionVariant="single">
            {Object.entries(sortOptions).map(([key, value]) => (
              <ActionList.Item key={key} selected={key === activeSort} onSelect={() => onSortChange(key)}>
                {value}
              </ActionList.Item>
            ))}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}

try{ Filters.displayName ||= 'Filters' } catch {}